import { flushSync } from "react-dom"
import { createRoot } from "react-dom/client"
import { METRIC_HEADER_FONT } from "../constants"

const waitForLayoutAndPaint = () => new Promise((resolve) => requestAnimationFrame(resolve))

/**
 * Measures the rendered width of a React node by temporarily rendering it in the DOM
 * @param {React.ReactNode} reactNode - The React node to measure
 * @param {Object} options - Measurement options
 * @param {boolean} options.nowrap - Whether to prevent text wrapping (default: true)
 * @param {Function} options.wrapperComponent - Optional wrapper function for the node
 * @param {string} options.font - Font to use for measurement (default: METRIC_HEADER_FONT)
 * @param {string} options.measureSelector - Measure the first matching descendant; a custom node without a match is measured whole
 * @returns {Promise<number>} The width of the rendered node in pixels
 */
export async function measureRenderNode(
  reactNode,
  options = {},
) {
  const {
    nowrap = true,
    font = METRIC_HEADER_FONT,
    measureSelector,
  } = options

  // Ensure fonts are loaded to prevent fallback font width measurements
  if ("fonts" in document) {
    await document.fonts.ready
  }

  // Create temporary container for measurement
  const measurementContainer = document.createElement("div")
  measurementContainer.classList.add("contributors-datagrid")
  Object.assign(measurementContainer.style, {
    position: "absolute",
    left: "-99999px",
    top: "0",
    width: "auto",
    height: "auto",
    visibility: "hidden",
    font,
    whiteSpace: nowrap ? "nowrap" : "normal",
    display: "inline-flex",
    alignItems: "center",
  })
  document.body.appendChild(measurementContainer)

  const reactRoot = createRoot(measurementContainer)
  try {
    flushSync(() => reactRoot.render(reactNode))

    await waitForLayoutAndPaint()

    const measuredElement = (measureSelector && measurementContainer.querySelector(measureSelector)) || measurementContainer
    return measuredElement.getBoundingClientRect().width
  } finally {
    reactRoot.unmount()
    measurementContainer.remove()
  }
}
